import React, { useEffect, useRef, useState } from "react";
import {
  SideFilter,
  ShopProducts,
  Pagination,
} from "../Components/index";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import useFetch from "../Network/useFetch";
import Loader from "../Components/Loader";
import { useRecoilState, useSetRecoilState } from "recoil";
import { FilterCheckBox, FilterInfo } from "../Components/atoms/atoms";

import { categoryLabel, useCategories } from "../Components/ShopCategories";
import { categoryPages, isCategoryId } from "../seo/categoryPages";
import { applyPageMeta, categoryMeta } from "../seo/pageMeta";
import { bringIntoView } from "../navigation/useScrollIntoViewOnChange";
import { useListParams } from "../navigation/useListParams";
import "../category-results.css";

const Shop = () => {
  // The address is a readable slug (/shop/power-supplies); old links use the id.
  const { categoryId: param = "" } = useParams();
  const categoriesQuery = useCategories();
  const byAddress = categoryPages(categoriesQuery.data || []);
  const category = isCategoryId(param) ? byAddress.find((c) => c.id === param) : byAddress.find((c) => c.slug === param);
  const categoryId = isCategoryId(param) ? param : category?.id || "";
  const unknownCategory = !isCategoryId(param) && !categoriesQuery.isLoading && !categoriesQuery.isError && !category;
  // Page and sort live in the address (?page=2&sort=low), so Back from a
  // product returns to the same page of the category.
  const list = useListParams({ sort: "" }, {sort:['latest','low','high']});
  const currentPage = list.page;
  const sortOption = list.get("sort");
  const [pageSize] = useState(8);
  const setFilterArray = useSetRecoilState(FilterCheckBox);
  const [filterValue, setFilterValue] = useRecoilState(FilterInfo);
  let fetchUrl = `${process.env.REACT_APP_SERVER_URL}/product/category?categoryId=${categoryId}&page=${currentPage}&pageSize=${pageSize}`;
  const fetchKey: any = `fetch-category`;
  if (filterValue?.filters) {
    const encodedFilter = encodeURIComponent(filterValue?.filters);
    fetchUrl = fetchUrl.concat(`&filter=${encodedFilter}`);
  }
  if (filterValue?.ranges) {
    fetchUrl = fetchUrl.concat(
      `&lowPrice=${filterValue?.ranges?.lowestPrice}&highPrice=${filterValue?.ranges?.highestPrice}`
    );
  }
  if (sortOption?.length) {
    fetchUrl = fetchUrl.concat(`&sort=${sortOption}`);
  }
  const { isLoading, data, isError, refetch } = useFetch(
    fetchUrl,
    [fetchKey, currentPage, categoryId, pageSize, filterValue, sortOption],
    {
      enabled: !!categoryId,
      keepPreviousData: true,
    }
  );
  const location = useLocation();
  const filterUrl: any = `${process.env.REACT_APP_SERVER_URL}/category/filter?categoryId=${categoryId}`;
  const fetchFilterKey: any = `fetch-filter-products`;
  const {
    isLoading: filterLoading,
    data: filterData,
    isError: isFilterError,
  } = useFetch(filterUrl, [fetchFilterKey, categoryId], {
    enabled: !!categoryId,
  });
  // A different category starts without filters (page and sort come from its address).
  useEffect(() => {
    setFilterValue(null);
    setFilterArray(null);
  }, [location.pathname, setFilterValue, setFilterArray]);

  // Choosing a filter starts the list again at page 1.
  const lastFilter = useRef(filterValue);
  useEffect(() => {
    if (lastFilter.current === filterValue) return;
    lastFilter.current = filterValue;
    if (filterValue && currentPage > 1) list.update({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when the filters change
  }, [filterValue]);

  const handlePageChange = (page: any) => {
    bringIntoView(document.querySelector<HTMLElement>(".cr-results"));
    list.update({ page });
  };

  const handleSortChange = (event) => {
    list.update({ sort: event.target.value, page: 1 });
  };

  const categoryTitle = category?.label || (data?.data?.title ? categoryLabel(data.data.title) : "");
  const canonicalPath = `/shop/${category?.slug || param}`;
  useEffect(() => {
    if (unknownCategory) applyPageMeta(location.pathname, { title: "Category not found", noindex: true });
    else applyPageMeta(canonicalPath, categoryMeta(categoryTitle));
  }, [location.pathname, canonicalPath, categoryTitle, unknownCategory]);

  // Old /shop/<id> links move to the readable address.
  if (isCategoryId(param) && category) return <Navigate to={canonicalPath} replace />;
  if (!categoryId && categoriesQuery.isError)
    return (
      <main className="category-results">
        <header className="cr-hero"><div className="cr-wrap">
          <h1>We couldn’t load this category</h1>
          <p><button type="button" onClick={() => categoriesQuery.refetch()}>Try again</button> or <Link to="/products">browse all products</Link>.</p>
        </div></header>
      </main>
    );
  if (unknownCategory)
    return (
      <main className="category-results">
        <header className="cr-hero"><div className="cr-wrap">
          <h1>Category not found</h1>
          <p>This category may have moved. <Link to="/products#browse-categories">Browse all categories</Link></p>
        </div></header>
      </main>
    );

  const total = data?.data?.data?.totalRecords ?? 0;
  const products = data?.data?.data?.results ?? [];
  return (
    <main className="category-results">
      <header className="cr-hero">
        <div className="cr-wrap">
          <Link to="/products#browse-categories">← Browse all categories</Link>
          <p className="cr-eyebrow">TECHNOGENIOUS / THE SHOP</p>
          <h1>{categoryTitle || "Product category"}</h1>
          <p>
            Explore parts and accessories. Need help checking compatibility?{" "}
            <Link to="/contact">Ask our team.</Link>
          </p>
        </div>
      </header>
      <div className="cr-wrap cr-layout">
        <details className="cr-filter" open>
          <summary>
            Filter products <span aria-hidden="true">⌄</span>
          </summary>
          {!data?.isSlider ? (
            filterLoading ? <p role="status">Loading filters…</p> : isFilterError ? <p>Filters are unavailable. You can still browse products.</p> : <SideFilter key={categoryId} isLoading={false} data={filterData} />
          ) : null}
        </details>

        <div className="cr-results">
          <div className="cr-toolbar">
            <p role="status">
              {isLoading
                ? "Loading products…"
                : total
                ? `Showing ${(currentPage - 1) * pageSize + 1}–${Math.min(
                    currentPage * pageSize,
                    total
                  )} of ${total} results`
                : "No products found"}
            </p>
            <select
              className="text-[#666666] font-light text-sm w-40 px-3 border-dotted py-2 border-black focus:border-2 focus:outline-none"
              aria-label="Sort category products"
              value={sortOption}
              onChange={handleSortChange}
            >
              <option value="">Default sorting</option>
              {/* <option value="popularity">Sort by popularity</option>
              <option value="rating">Sort by average rating</option> */}
              <option value="latest">Sort by latest</option>
              <option value="low">Sort by price: low to high</option>
              <option value="high">Sort by price: high to low</option>
            </select>
          </div>
          {isLoading ? (
            <div className="cr-message">
              <Loader forProduct />
            </div>
          ) : isError ? (
            <div className="cr-message" role="alert">
              <h2>We couldn’t load the products</h2>
              <p>Please try again.</p>
              <button onClick={() => refetch()}>Try again</button>
            </div>
          ) : products.length ? (
            <ShopProducts data={products} />
          ) : (
            <div className="cr-message">
              <h2>No matching products</h2>
              <p>Try clearing your filters or choosing another category.</p>
              <button
                onClick={() => {
                  setFilterValue(null);
                  setFilterArray(null);
                }}
              >
                Clear filters
              </button>
              <Link to="/products#browse-categories">Browse categories →</Link>
            </div>
          )}
          <div className="w-full flex items-center py-8">
            {total > pageSize ? (
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(total / pageSize)}
                onPageChange={handlePageChange}
              />
            ) : null}
          </div>
        </div>
      </div>
    </main>
  );
};

export default Shop;
